(() => {
  'use strict';

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundResetATC = this.resetATC.bind(this);
      this.boundOptionChange = this.handleOptionChange.bind(this);
      this.boundScroll = this.toggleScroll.bind(this);
    }

    connectedCallback() {
      this.section = this.getSectionElement();
      this.sectionId = this.getSectionId();
      this.form = this.section?.querySelector('[data-product-form]');

      if (!this.section || !this.form) {
        return;
      }

      this.formWrapper =
        (window.theme?.settings?.productPageSticky && this.section.querySelector('[data-form-wrapper]')) || this.form;
      this.image = this.querySelector('[data-cart-bar-image]');
      this.options = this.querySelector('[data-cart-bar-options]');
      this.qty = this.querySelector('[data-cart-bar-qty]');
      this.atcBtn = this.querySelector('[data-cart-bar-add-to-cart]');

      this.addEventListener('click', this.boundHandleClick);
      ['theme:cart:refresh', 'theme:cart:added', 'theme:cart-bar:reset-atc-animation'].forEach((eventName) => {
        document.addEventListener(eventName, this.boundResetATC);
      });

      this.initOptions();
      this.initQty();
      this.subVariant();
      this.initObserver();
    }

    getSectionElement() {
      const nearestSection =
        this.closest('product-info') ||
        this.closest('[data-product]') ||
        this.closest('[data-section-id]') ||
        this.closest('[data-section]');

      if (nearestSection) {
        return nearestSection;
      }

      const cartBarId = this.id || '';
      const sectionId = cartBarId.startsWith('CartBar-') ? cartBarId.replace('CartBar-', '') : '';

      if (!sectionId) {
        return null;
      }

      return (
        document.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`) ||
        document.querySelector(`[data-section="${CSS.escape(sectionId)}"]`) ||
        document.querySelector(`[data-original-section="${CSS.escape(sectionId)}"]`)
      );
    }

    getSectionId() {
      const cartBarId = this.id || '';
      const idFromCartBar = cartBarId.startsWith('CartBar-') ? cartBarId.replace('CartBar-', '') : '';
      const idFromSection =
        this.section?.dataset?.section ||
        this.section?.dataset?.sectionId ||
        this.section?.dataset?.originalSection ||
        '';

      return idFromSection || idFromCartBar;
    }

    resetATC() {
      this.atcBtn?.classList.remove('is-loading');
      this.atcBtn?.removeAttribute('disabled');
    }

    handleClick(event) {
      if (event.target.closest('[data-scroll-to-form]')) {
        event.preventDefault();

        const focusTarget =
          this.section.querySelector('product-form') ||
          this.section.querySelector('[data-form-wrapper]') ||
          this.section.querySelector('[data-product-form]');

        if (!focusTarget) {
          return;
        }

        if (typeof focusTarget.setAttribute === 'function') {
          focusTarget.setAttribute('tabindex', '-1');
        }

        if (typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }

        if (typeof focusTarget.scrollIntoView === 'function') {
          focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        return;
      }

      if (event.target.closest('[data-cart-bar-add-to-cart]')) {
        event.preventDefault();
        const submitButton = this.form.querySelector('[data-add-to-cart]');

        if (!submitButton || submitButton.hasAttribute('disabled')) {
          return;
        }

        this.atcBtn?.classList.add('is-loading');
        this.atcBtn?.setAttribute('disabled', 'disabled');

        submitButton.click();

        if (this.atcResetTimer) {
          clearInterval(this.atcResetTimer);
        }

        let pollCount = 0;
        this.atcResetTimer = setInterval(() => {
          pollCount += 1;
          const stillLoading = submitButton.classList.contains('is-loading');
          const stillDisabled = submitButton.hasAttribute('disabled');

          if ((!stillLoading && !stillDisabled) || pollCount >= 50) {
            clearInterval(this.atcResetTimer);
            this.atcResetTimer = null;
            this.atcBtn?.classList.remove('is-loading');
            this.atcBtn?.removeAttribute('disabled');
          }
        }, 100);
      }
    }

    initOptions() {
      this.options?.querySelectorAll('[data-popout-input]').forEach((input) => {
        input.addEventListener('change', this.boundOptionChange);
      });
    }

    handleOptionChange(event) {
      const optionValueId = event.target.getAttribute('data-option-value-id');

      if (!optionValueId) {
        return;
      }

      const optionTarget = this.section.querySelector(`variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`);

      if (!optionTarget) {
        return;
      }

      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
      clickEvent.fromCartBar = true;

      if (optionTarget.tagName === 'A' || optionTarget.matches('input')) {
        optionTarget.dispatchEvent(clickEvent);

        if (optionTarget.matches('input')) {
          const changeEvent = new Event('change', { bubbles: true });
          changeEvent.fromCartBar = true;
          optionTarget.dispatchEvent(changeEvent);
        }

        return;
      }

      optionTarget.closest('label, a, [role="button"], .select-popout__option')?.dispatchEvent(clickEvent);
    }

    subVariant() {
      if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) {
        return;
      }

      this.unsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const changedSectionId = data?.sectionId;

        if (changedSectionId && this.sectionId && changedSectionId !== this.sectionId) {
          return;
        }

        this.updateImage(data?.variant);
        this.updateOptions(data?.variant);
        this.updateBtn(data?.variant);
        this.updatePrice(data?.variant);
      });
    }

    updateImage(variant) {
      const imageEl = this.image?.querySelector('img');
      const imageSrc = variant?.featured_media?.preview_image?.src;

      if (!imageEl || !imageSrc) {
        return;
      }

      imageEl.src = `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}width=160`;
    }

    updateOptions(variant) {
      if (!this.options || !variant?.options) {
        return;
      }

      this.options.querySelectorAll('popout-select').forEach((popout) => {
        const popoutInput = popout.querySelector('[data-popout-input]');
        const optionIndex = parseInt(popoutInput?.dataset.index?.replace('option', '') || '', 10);

        if (!popoutInput || Number.isNaN(optionIndex)) {
          return;
        }

        const variantOptionValue = variant.options[optionIndex - 1];
        const popoutText = popout.querySelector('[data-popout-text]');

        popout.querySelectorAll('[data-popout-option]').forEach((option) => {
          const optionValue = option.dataset.value;
          const isCurrent = optionValue === variantOptionValue;
          const listItem = option.closest('li');

          if (isCurrent && listItem) {
            if (popoutText) {
              popoutText.textContent = optionValue;
            }

            popoutInput.value = optionValue;

            if (listItem.dataset.optionValueId) {
              popoutInput.setAttribute('data-option-value-id', listItem.dataset.optionValueId);
            }

            if (listItem.dataset.productUrl) {
              popoutInput.setAttribute('data-product-url', listItem.dataset.productUrl);
            }

            popout.querySelector('.select-popout__item--current')?.classList.remove('select-popout__item--current');
            listItem.classList.add('select-popout__item--current');
            popout.querySelector('[aria-current="true"]')?.removeAttribute('aria-current');
            option.setAttribute('aria-current', 'true');
          }

          const thisOptionValueId = listItem?.dataset.optionValueId;

          if (!thisOptionValueId) {
            return;
          }

          const sourceOption = this.section.querySelector(
            `variant-selects [data-option-value-id="${CSS.escape(thisOptionValueId)}"]`
          );

          if (!sourceOption) {
            return;
          }

          const unavailable =
            Boolean(sourceOption.closest('.sold-out, .unavailable')) ||
            sourceOption.classList.contains('unavailable') ||
            sourceOption.disabled;

          option.classList.toggle('unavailable', unavailable);
        });
      });
    }

    updateBtn(variant) {
      const addToCartText = this.querySelector('[data-add-to-cart-text]');

      if (!this.atcBtn || !addToCartText || !variant) {
        return;
      }

      const available = variant.available;
      addToCartText.textContent =
        window.theme?.strings?.[available ? 'addToCart' : 'soldOut'] || (available ? 'Add to cart' : 'Sold out');
      this.atcBtn.toggleAttribute('disabled', !available);
    }

    updatePrice(variant) {
      const priceNode = this.querySelector('[data-product-price]');

      if (!priceNode || !variant) {
        return;
      }

      const formatMoney = (value) =>
        window.theme.formatMoney(
          value,
          window.theme?.settings?.currency_code_enable ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat
        );

      priceNode.innerHTML = variant.price === 0 ? window.theme?.strings?.free || 'Free' : formatMoney(variant.price);

      const isOnSale = variant.compare_at_price > variant.price;
      priceNode.classList.toggle('product__price--sale', isOnSale);

      const comparePriceNode = this.querySelector('.product__price--strike');

      if (comparePriceNode) {
        comparePriceNode.textContent = isOnSale ? formatMoney(variant.compare_at_price) : '';
        comparePriceNode.classList.toggle('hidden', !isOnSale);
      }
    }

    initQty() {
      if (!this.qty) {
        return;
      }

      const cartBarQtyField = this.qty.querySelector('[data-quantity-field]');
      const mainQtyField = Array.from(this.section.querySelectorAll('[data-quantity-holder]'))
        .find((holder) => !this.contains(holder))
        ?.querySelector('[data-quantity-field]');

      if (!cartBarQtyField || !mainQtyField) {
        return;
      }

      cartBarQtyField.value = mainQtyField.value;
      this.qtySync = false;

      const syncFields = (source, target) => {
        if (this.qtySync) {
          return;
        }

        this.qtySync = true;
        target.value = source.value;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        this.qtySync = false;
      };

      this.syncMain = () => syncFields(mainQtyField, cartBarQtyField);
      this.syncBar = () => syncFields(cartBarQtyField, mainQtyField);

      [mainQtyField, cartBarQtyField].forEach((field) => {
        ['input', 'change'].forEach((eventName) => {
          field.addEventListener(eventName, field === mainQtyField ? this.syncMain : this.syncBar);
        });
      });

      this.mainQtyRef = mainQtyField;
      this.barQtyRef = cartBarQtyField;
    }

    initObserver() {
      if ('IntersectionObserver' in window && this.formWrapper) {
        this.obs = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.obs.observe(this.formWrapper);
        return;
      }

      document.addEventListener('theme:scroll', this.boundScroll);
    }

    toggleScroll() {
      if (!this.formWrapper) {
        return;
      }

      const shouldShow = window.scrollY > this.formWrapper.offsetTop + this.formWrapper.offsetHeight;
      this.classList.toggle('is-visible', shouldShow);
    }

    disconnectedCallback() {
      ['theme:cart:refresh', 'theme:cart:added', 'theme:cart-bar:reset-atc-animation'].forEach((eventName) => {
        document.removeEventListener(eventName, this.boundResetATC);
      });

      /** InsCoder 2026-07-31 B + Clear pending atc reset timer on disconnect **/
      if (this.atcResetTimer) {
        clearInterval(this.atcResetTimer);
        this.atcResetTimer = null;
      }
      /** InsCoder 2026-07-31 E + Clear pending atc reset timer on disconnect **/

      if (this.obs) {
        this.obs.disconnect();
      } else {
        document.removeEventListener('theme:scroll', this.boundScroll);
      }

      this.unsub?.();
      this.removeEventListener('click', this.boundHandleClick);

      this.options?.querySelectorAll('[data-popout-input]').forEach((input) => {
        input.removeEventListener('change', this.boundOptionChange);
      });

      if (this.mainQtyRef) {
        ['input', 'change'].forEach((eventName) => {
          this.mainQtyRef.removeEventListener(eventName, this.syncMain);
        });
      }

      if (this.barQtyRef) {
        ['input', 'change'].forEach((eventName) => {
          this.barQtyRef.removeEventListener(eventName, this.syncBar);
        });
      }
    }
  }

  class QuantityCounter extends HTMLElement {
    constructor() {
      super();
      this.boundOnButtonClick = this.onButtonClick.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.buttons = this.querySelectorAll('button');

      this.buttons.forEach((button) =>
        button.addEventListener('click', this.boundOnButtonClick)
      );
    }

    disconnectedCallback() {
      this.buttons.forEach((button) =>
        button.removeEventListener('click', this.boundOnButtonClick)
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;
      const button = event.target.closest('button');

      if (button.hasAttribute('data-quantity-plus')) {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }

  if (!customElements.get('quantity-counter')) {
    customElements.define('quantity-counter', QuantityCounter);
  }

  if (!customElements.get('cart-bar')) {
    customElements.define('cart-bar', CartBar);
  }
})();
